import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  InputLabel,
  TextField,
  MenuItem,
  Box,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { AppStatus } from 'utils/dropDownList';
import {
  getAppointment,
  getProviderOfficeExamRoom,
  updateAppointment,
} from 'store/slices/patientInfoSlice';
import { useAppDispatch } from 'store/hooks';
import { toast } from 'react-toastify';
import { StyledDialog } from 'theme/index';
import { SCHEDULING_UNAVAILABLE_MESSAGE } from 'shared/content/constants';

export type EditAppointmentPayload = {
  id: number;
  doctor: number;
  office: number;
  profile: number;
  exam_room: number;
  duration: number;
  status: string;
  scheduled_time: string | Date;
  patientName: string;
  reason: string;
};
export type EditAppointmentProps = {
  appointmentId: number;
  offices: any[];
  providers: any[];
  profiles: any[];
  loading?: boolean;
  setLoading?: (val: boolean) => void;
  onClose?: () => void;
  onUpdateAppointment?: (data: any) => void;
};

const EditAppointment = (props: EditAppointmentProps) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const officeList = props.offices || [];
  const providerList = props.providers || [];
  const profileList = props.profiles || [];
  const [examRoomList, setExamRoomList] = useState<any[]>([]);
  const appointmentId = props.appointmentId;
  const isEditMode = appointmentId > 0;
  const [editAppointmentPayload, setEditAppointmentPayload] =
    useState<EditAppointmentPayload | null>(null);
  const dispatch = useAppDispatch();
  const [formData, setFormData] = useState({
    doctor: 0,
    office: 0,
    profile: 0,
    examRoom: 0,
    date: null as Dayjs | null,
    time: null as Dayjs | null,
    duration: 0,
    status: '',
    patientName: '',
    reason: '',
  });
  useEffect(() => {
    if (appointmentId > 0) {
      dispatch(getAppointment({ appointmentId }))
        .unwrap()
        .then((response: any) => {
          if (response?.status === 200) {
            setEditAppointmentPayload(response?.data);
            const officeId = response?.data?.office;
            dispatch(getProviderOfficeExamRoom({ officeId }))
              .unwrap()
              .then((res: any) => {
                if (res?.status === 200) {
                  setExamRoomList(res?.data || []);
                }
              });
          }
        });
    }
  }, [appointmentId, dispatch]);

  // ✅ Populate data when editing
  useEffect(() => {
    if (editAppointmentPayload && isEditMode) {
      const dateTime = dayjs(editAppointmentPayload.scheduled_time);
      setFormData({
        doctor: editAppointmentPayload.doctor,
        office: editAppointmentPayload.office,
        profile: editAppointmentPayload.profile,
        examRoom: editAppointmentPayload.exam_room,
        date: dateTime,
        time: dateTime,
        duration: editAppointmentPayload.duration || 0,
        status: editAppointmentPayload.status || '',
        reason: editAppointmentPayload.reason || '',
        patientName: editAppointmentPayload.patientName || '',
      });
      setAppointmentsLoading(true);
      props.setLoading?.(false); // 🔥 stop loader
    }
  }, [editAppointmentPayload, isEditMode]);

  // ✅ Handle change
  const handleChange =
    (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        field === 'doctor' || field === 'office' || field === 'profile' || field === 'duration'
          ? Number(event.target.value)
          : event.target.value;

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  // ✅ Date change
  const handleDateChange = (value: Dayjs | null) => {
    setFormData((prev) => ({ ...prev, date: value }));
  };

  // ✅ Time change
  const handleTimeChange = (value: Dayjs | null) => {
    setFormData((prev) => ({ ...prev, time: value }));
  };

  // ✅ Close dialog
  const onClose = () => {
    props.onClose?.();
  };

  // ✅ Save handler
  const handleSave = () => {
    const scheduled_time =
      formData.date && formData.time
        ? dayjs(formData.date)
            .hour(formData.time.hour())
            .minute(formData.time.minute())
            .second(0)
            .format('YYYY-MM-DDTHH:mm:ss')
        : null;

    const payload = {
      ...formData,
      scheduled_time: scheduled_time,
      appointmentId,
    };
    editAppointment(payload);
  };

  const editAppointment = (payload: any) => {
    dispatch(updateAppointment(payload))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          if (response.data.apiResult) {
            toast.success('Appointment updated successfully');
            props.onUpdateAppointment?.(payload);
            return;
          } else if (response.data.throttled) {
            toast.error(SCHEDULING_UNAVAILABLE_MESSAGE);
          } else if (response.data.messsage.toLowerCase().includes('overlap')) {
            toast.error('Appointment overlaps with an existing appointment');
          } else {
            toast.error(response.data.messsage);
          }
        } else {
          toast.error('Failed to update appointment');
        }
      });
  };
  return (
    <StyledDialog open={true} onClose={onClose} maxWidth='md' fullWidth>
      {/* 🔥 Loader Overlay */}
      <Backdrop
        open={props.loading || false}
        sx={{
          position: 'absolute',
          zIndex: 1300,
          color: '#fff',
        }}
      >
        <CircularProgress color='inherit' />
      </Backdrop>
      <DialogTitle>{isEditMode ? 'Edit' : 'Create'} Appointment</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Provider */}
          <Grid item xs={4}>
            <InputLabel required>Provider</InputLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={formData.doctor}
              onChange={handleChange('doctor')}
              disabled={true}
            >
              {providerList.map((p: any) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* Patient Name */}
          <Grid item xs={4}>
            <InputLabel required>Patient</InputLabel>
            <TextField
              fullWidth
              size='small'
              value={formData.patientName}
              onChange={handleChange('patientName')}
              disabled={true}
            />
          </Grid>
          {/* Profile */}
          <Grid item xs={4}>
            <InputLabel required>Appointment Template</InputLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={formData.profile}
              onChange={handleChange('profile')}
              disabled={true}
            >
              {profileList.map((p: any) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* Profile */}
          <Grid item xs={4}>
            <InputLabel required>Exam Room</InputLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={formData.examRoom}
              onChange={handleChange('examRoom')}
              disabled={true}
            >
              {examRoomList.map((p: any) => (
                <MenuItem key={p.index} value={p.index}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Office */}
          <Grid item xs={4}>
            <InputLabel required>Office</InputLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={formData.office}
              onChange={handleChange('office')}
              disabled={true}
            >
              {officeList.map((o: any) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* Patient Name */}
          <Grid item xs={4}>
            <InputLabel>Reason</InputLabel>
            <TextField
              fullWidth
              size='small'
              value={formData.reason}
              onChange={handleChange('reason')}
            />
          </Grid>
          {/* Date */}
          <Grid item xs={3}>
            <InputLabel required>Schedule</InputLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={formData.date}
                onChange={handleDateChange}
                slotProps={{
                  textField: { fullWidth: true, size: 'small' },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Time */}
          <Grid item xs={2}>
            <InputLabel required>Time </InputLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <TimePicker
                disabled={true}
                value={formData.time}
                onChange={handleTimeChange}
                slotProps={{
                  textField: { fullWidth: true, size: 'small' },
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Duration */}
          <Grid item xs={2}>
            <InputLabel>Duration(minutes)</InputLabel>
            <TextField
              type='number'
              fullWidth
              size='small'
              value={formData.duration}
              onChange={handleChange('duration')}
            />
          </Grid>

          {/* Status */}
          <Grid item xs={3}>
            <InputLabel required>Status</InputLabel>
            <TextField
              select
              fullWidth
              size='small'
              value={formData.status}
              onChange={handleChange('status')}
            >
              <MenuItem key={'Scheduled'} value={'Scheduled'}>
                Scheduled
              </MenuItem>
              {AppStatus.map((status: any) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button variant='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='contained' onClick={handleSave}>
          OK
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default EditAppointment;
